import { useMemo } from 'react';

import {
  DATA_SOURCE_ICONS,
  DATA_SOURCE_IDS,
  DATA_SOURCE_LABELS,
  QUERY_TYPE_IDS,
  QUERY_TYPE_ICONS,
  QUERY_TYPE_LABELS,
} from '@discover-frontend/constants';
import { RepositoryConstants } from 'src/constants/repository';
import { isEmpty } from 'src/utils';
import { isAvailableQueryBuilderType } from 'src/utils/AuthHelperMethods';

interface FiltersProps {
  filter: string;
  onFilterChange: (value: string) => void;
  productDataSources: Array<string>;
  isMyCompanyLoading: boolean;
  isMriSimmonsActivated: boolean;
}

export const useFilters = ({
  filter,
  onFilterChange,
  productDataSources,
  isMyCompanyLoading,
  isMriSimmonsActivated,
}: FiltersProps) => {
  return {
    filters: useMemo(() => {
      let items = [];

      if (isMyCompanyLoading) {
        return items;
      }

      const isInterestAvailableNotEmpty =
        isAvailableQueryBuilderType(RepositoryConstants.INTEREST) &&
        !isEmpty(productDataSources);

      const isInterestAvailableEmpty =
        isAvailableQueryBuilderType(RepositoryConstants.INTEREST) &&
        isEmpty(productDataSources);

      const isConversationAvailable = isAvailableQueryBuilderType(
        RepositoryConstants.CONVERSATION
      );

      const isTSVAvailable = isAvailableQueryBuilderType(
        RepositoryConstants.TSV
      );

      const filterOptions = [
        {
          iconLeft: DATA_SOURCE_ICONS.TWITTER,
          label: DATA_SOURCE_LABELS.TWITTER,
          value: DATA_SOURCE_IDS.TWITTER,
          condition: isInterestAvailableNotEmpty,
        },
        {
          label: DATA_SOURCE_LABELS.APPS,
          value: DATA_SOURCE_IDS.APPS,
          iconLeft: DATA_SOURCE_ICONS.APPS,
          condition:
            isInterestAvailableNotEmpty &&
            productDataSources.includes(DATA_SOURCE_IDS.APPS),
        },
        {
          label: DATA_SOURCE_LABELS.INSTAGRAM,
          value: DATA_SOURCE_IDS.INSTAGRAM,
          iconLeft: DATA_SOURCE_ICONS.INSTAGRAM,
          condition:
            isInterestAvailableNotEmpty &&
            productDataSources.includes(DATA_SOURCE_IDS.INSTAGRAM),
        },
        {
          label: DATA_SOURCE_LABELS.INSTAGRAM_FOLLOWS,
          value: DATA_SOURCE_IDS.INSTAGRAM_FOLLOWS,
          iconLeft: DATA_SOURCE_ICONS.INSTAGRAM_FOLLOWS,
          condition:
            isInterestAvailableNotEmpty &&
            productDataSources.includes(DATA_SOURCE_IDS.INSTAGRAM_FOLLOWS),
        },
        {
          label: DATA_SOURCE_LABELS.BROWSER,
          value: DATA_SOURCE_IDS.BROWSER,
          iconLeft: DATA_SOURCE_ICONS.BROWSER,
          condition:
            isInterestAvailableNotEmpty &&
            productDataSources.includes(DATA_SOURCE_IDS.BROWSER),
        },
        {
          iconLeft: 'heart',
          label: 'Interests',
          value: 'recent',
          condition: isInterestAvailableEmpty,
        },
        {
          iconLeft: 'conversation',
          label: 'Conversation',
          value: 'conversation',
          condition: isConversationAvailable,
        },
        {
          iconLeft: 'users',
          label: 'TSV',
          value: 'tsv',
          condition: isTSVAvailable,
        },
        {
          label: QUERY_TYPE_LABELS.CAMPAIGN,
          value: QUERY_TYPE_IDS.CAMPAIGN,
          iconLeft: QUERY_TYPE_ICONS.CAMPAIGN,
          condition: isMriSimmonsActivated,
        },
        'divider',
        {
          iconLeft: 'folder',
          label: 'My Folders',
          value: 'my',
          condition: true,
        },
      ];

      items = filterOptions
        .filter(item => typeof item === 'string' || item.condition) // Handle both strings (like 'divider') and objects
        .map(item =>
          typeof item === 'string'
            ? item
            : {
                ...item,
                isActive: filter === item.value,
                onClick: () => onFilterChange(item.value),
              }
        );

      return items;
    }, [
      filter,
      onFilterChange,
      productDataSources,
      isMyCompanyLoading,
      isMriSimmonsActivated,
    ]),
  };
};
